import { trackActivationMilestone, trackFeatureUsed } from '@/lib/analytics'
import { noteEngagementActivity } from '@/lib/engagement/activity-signal'
import { createLogger } from '@/lib/logger'
import { desktopSecureStorage, getPlatform, isTauri } from '@/lib/utils/tauri'
import { useOrganizationStore } from '@/store/organization.store'
import { env } from '@/utils/env'

/**
 * Time Entries API service using BaseApi pattern
 * Includes approval workflow methods
 */

import type {
  ImportOptions,
  ImportPreviewRecord,
  ImportResult,
  TimeReportSummary,
} from '@scrambl/shared'
import type { SessionSnapshot } from '@scrambl/shared/session-scope'
import {
  BulkApprovalResponse,
  CreateTimeEntry,
  PaginatedResponse,
  PaginationParams,
  SplitTimeEntryInput,
  SwitchTimerItemInput,
  TimeConflictGroup,
  TimeEntry,
  TimeEntryApprovalHistory,
  TimeEntryOperationReceipt,
  TimeEntryTypeEnum,
  UpdateTimeEntryInput,
} from '@scrambl/shared'

import { ApiError } from './api-error'
import { BaseApi } from './base-api'
import { getErrorStatus, isLostResponseError } from './error-utils'
import { fetchAllPages } from './paginate'
import { captureRequestScope } from './request-scope'
import { createSingletonApi } from './singleton-factory'

/**
 * Create payload where `billable` is optional: when it is omitted the API
 * applies the resolved default (project > client > internal rule > org).
 */
export type CreateTimeEntryRequest = Omit<CreateTimeEntry, 'billable'> & {
  billable?: boolean
}

/** Which level of the billable-default chain decided the value. */
export type DefaultBillableSource =
  | 'CATEGORY'
  | 'PROJECT'
  | 'CLIENT'
  | 'INTERNAL'
  | 'ORGANIZATION'

export interface DefaultBillableResult {
  billable: boolean
  source: DefaultBillableSource
}

export interface BulkUpdateTimeEntriesInput {
  ids: string[]
  billable?: boolean
  /** A time category id; null clears the category. */
  categoryId?: string | null
}

export interface BulkUpdateTimeEntriesResult {
  updated: string[]
  skipped: Array<{ id: string; reason: string }>
}

export interface TimeReportExportOptions {
  scope?: 'TEAM' | 'PERSONAL'
  session?: SessionSnapshot
  requestId?: string
}

const logger = createLogger('lib/api/time-entries.api')

class TimeEntryApiService extends BaseApi<
  TimeEntry,
  CreateTimeEntryRequest,
  UpdateTimeEntryInput
> {
  constructor() {
    super({
      baseEndpoint: '/time-entries',
      responseNormalization: true,
    })
  }

  // Alias methods for backward compatibility
  async getTimeEntries(
    params: Partial<PaginationParams> = {},
  ): Promise<PaginatedResponse<TimeEntry>> {
    return this.list(params)
  }

  async getAllTimeEntries(
    params: Partial<PaginationParams> & Record<string, unknown> = {},
    pageSize = 200,
  ): Promise<PaginatedResponse<TimeEntry>> {
    const scope = captureRequestScope()
    let lastMeta: PaginatedResponse<TimeEntry>['meta'] | undefined
    const data = await fetchAllPages(async (page, limit) => {
      scope.assertCurrent()
      const response = await this.getTimeEntries({ ...params, page, limit })
      scope.assertCurrent()
      lastMeta = response.meta
      return response
    }, pageSize)

    return {
      data,
      meta: {
        ...lastMeta,
        totalItems: data.length,
        currentPage: 1,
        itemsPerPage: Math.max(data.length, 1),
        totalPages: data.length > 0 ? 1 : 0,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    }
  }

  async getTimeEntry(id: string): Promise<TimeEntry> {
    return this.findById(id)
  }

  async getTaskTimeTotal(
    taskId: string,
  ): Promise<{ taskId: string; totalHours: number; entryCount: number }> {
    return this.makeRequest<{
      taskId: string
      totalHours: number
      entryCount: number
    }>(`${this.config.baseEndpoint}/tasks/${taskId}/my-total`)
  }

  /**
   * Every logical write carries one operation ID. When the response is lost
   * (timeout, dropped connection, gateway error) the saved receipt decides:
   * an applied write returns its entry, and an absent receipt is resent once
   * with the same ID, which the server deduplicates.
   */
  async createTimeEntry(data: CreateTimeEntryRequest): Promise<TimeEntry> {
    const request = {
      ...data,
      operationId: data.operationId ?? crypto.randomUUID(),
    }
    const created = await this.sendWithReceiptRecovery(
      request.operationId,
      () => this.create(request),
    )

    // Manual entries can have duration only; running timers become logged
    // time when stopped (see below).
    if (data.entryType === TimeEntryTypeEnum.MANUAL || data.endTime) {
      trackActivationMilestone('time_logged', { source: 'manual' })
      noteEngagementActivity('time_logged')
    } else {
      trackFeatureUsed('time_timer', {
        action: 'start',
        module: data.moduleType ?? 'none',
      })
    }

    return created
  }

  async updateTimeEntry(
    id: string,
    data: UpdateTimeEntryInput,
  ): Promise<TimeEntry> {
    const request = {
      ...data,
      operationId: data.operationId ?? crypto.randomUUID(),
    }
    const updated = await this.sendWithReceiptRecovery(
      request.operationId,
      () => this.update(id, request),
    )

    // Stopping a timer is a PATCH that sets endTime — this is the single
    // point both TimeEntryProvider.stopTimer and the Benny companion share.
    if (data.endTime) {
      trackActivationMilestone('time_logged', { source: 'timer' })
      noteEngagementActivity('time_logged')
    }

    return updated
  }

  /** The saved outcome of a write sent with this operation ID, or null. */
  async getOperationReceipt(
    operationId: string,
  ): Promise<TimeEntryOperationReceipt | null> {
    try {
      return await this.makeRequest<TimeEntryOperationReceipt>(
        `${this.config.baseEndpoint}/operations/${encodeURIComponent(operationId)}`,
      )
    } catch (error) {
      if (getErrorStatus(error) === 404) return null
      throw error
    }
  }

  private async sendWithReceiptRecovery(
    operationId: string,
    send: () => Promise<TimeEntry>,
  ): Promise<TimeEntry> {
    try {
      return await send()
    } catch (error) {
      if (!isLostResponseError(error)) throw error
      let receipt: TimeEntryOperationReceipt | null
      try {
        receipt = await this.getOperationReceipt(operationId)
      } catch {
        // Still unknown: keep the original failure for the caller's review.
        throw error
      }
      if (receipt?.entry) return receipt.entry
      // Saved, but no longer readable (e.g. deleted since): never write it again.
      if (receipt)
        throw new ApiError(
          'This time entry was saved, but it is no longer available.',
          410,
        )
      logger.warn('Resending a time entry write whose outcome was lost', {
        operationId,
      })
      return send()
    }
  }

  async deleteTimeEntry(id: string): Promise<void> {
    return this.delete(id)
  }

  /**
   * The billable value a new entry linked to this record gets when the
   * caller doesn't choose one. No module = time without a client. A time
   * category's default wins over the project and client.
   */
  async getDefaultBillable(
    moduleType?: string | null,
    moduleId?: string | null,
    categoryId?: string | null,
  ): Promise<DefaultBillableResult> {
    const params = new URLSearchParams()
    if (moduleType && moduleId) {
      params.set('moduleType', moduleType)
      params.set('moduleId', moduleId)
    }
    if (categoryId) {
      params.set('categoryId', categoryId)
    }
    const query = params.toString()
    const response = await this.makeRequest<
      DefaultBillableResult | { data: DefaultBillableResult }
    >(`${this.config.baseEndpoint}/default-billable${query ? `?${query}` : ''}`)
    return 'data' in response && response.data
      ? response.data
      : (response as DefaultBillableResult)
  }

  /**
   * Reclassify several entries at once. Approved or invoiced entries are
   * skipped by the API and reported back with a reason.
   */
  async bulkUpdateTimeEntries(
    input: BulkUpdateTimeEntriesInput,
  ): Promise<BulkUpdateTimeEntriesResult> {
    const response = await this.makeRequest<
      BulkUpdateTimeEntriesResult | { data: BulkUpdateTimeEntriesResult }
    >(`${this.config.baseEndpoint}/bulk`, {
      method: 'PATCH',
      body: input,
    })
    const result =
      'data' in response && response.data
        ? response.data
        : (response as BulkUpdateTimeEntriesResult)
    return {
      updated: result.updated ?? [],
      skipped: result.skipped ?? [],
    }
  }

  // Timer-specific methods
  async getActiveTimer(): Promise<TimeEntry | null> {
    try {
      const response = await this.makeRequest<TimeEntry | null>(
        `${this.config.baseEndpoint}/active`,
      )
      if (!response || typeof response !== 'object' || !('id' in response)) {
        return null
      }
      return response
    } catch (error) {
      logger.error('[TimeEntryApi] Error fetching active timer:', error)
      return null
    }
  }

  // Overlap (double-tracking) conflicts for the current user.
  async getMyTimeConflicts(params: {
    startDate: string
    endDate: string
  }): Promise<{ groups: TimeConflictGroup[] }> {
    const query = new URLSearchParams({
      startDate: params.startDate,
      endDate: params.endDate,
    }).toString()
    return this.makeRequest<{ groups: TimeConflictGroup[] }>(
      `${this.config.baseEndpoint}/conflicts?${query}`,
    )
  }

  // Overlap conflicts for a specific team member (manager view).
  async getTeamTimeConflicts(params: {
    userId: string
    startDate: string
    endDate: string
  }): Promise<{ groups: TimeConflictGroup[] }> {
    const query = new URLSearchParams({
      userId: params.userId,
      startDate: params.startDate,
      endDate: params.endDate,
    }).toString()
    return this.makeRequest<{ groups: TimeConflictGroup[] }>(
      `${this.config.baseEndpoint}/team/conflicts?${query}`,
    )
  }

  async updateHeartbeat(
    id: string,
  ): Promise<{ success: boolean; active: boolean }> {
    return this.makeRequest<{ success: boolean; active: boolean }>(
      `${this.config.baseEndpoint}/${id}/heartbeat`,
      {
        method: 'PUT',
      },
    )
  }

  async pauseTimer(id: string): Promise<TimeEntry> {
    const response = await this.makeRequest<TimeEntry | { data: TimeEntry }>(
      `${this.config.baseEndpoint}/${id}/pause`,
      {
        method: 'PUT',
      },
    )
    return this.normalizeResponse(response)
  }

  async resumeTimer(id: string): Promise<TimeEntry> {
    const response = await this.makeRequest<TimeEntry | { data: TimeEntry }>(
      `${this.config.baseEndpoint}/${id}/resume`,
      {
        method: 'PUT',
      },
    )
    return this.normalizeResponse(response)
  }

  async splitTimeEntry(
    id: string,
    data: SplitTimeEntryInput,
  ): Promise<TimeEntry[]> {
    const response = await this.makeRequest<
      TimeEntry[] | { data: TimeEntry[] }
    >(`${this.config.baseEndpoint}/${id}/split`, {
      method: 'POST',
      body: data,
    })

    if (Array.isArray(response)) {
      return response
    }
    return response.data || []
  }

  async switchTimerItem(
    id: string,
    data: SwitchTimerItemInput,
  ): Promise<TimeEntry> {
    const response = await this.makeRequest<TimeEntry | { data: TimeEntry }>(
      `${this.config.baseEndpoint}/${id}/switch-item`,
      {
        method: 'PUT',
        body: data,
      },
    )

    return this.normalizeResponse(response)
  }

  // =====================
  // Approval Workflow Methods
  // =====================

  async submitForApproval(id: string): Promise<TimeEntry> {
    const response = await this.makeRequest<TimeEntry | { data: TimeEntry }>(
      `${this.config.baseEndpoint}/${id}/submit`,
      {
        method: 'PUT',
      },
    )
    return this.normalizeResponse(response)
  }

  async requestChanges(id: string, comment: string): Promise<TimeEntry> {
    const response = await this.makeRequest<TimeEntry | { data: TimeEntry }>(
      `${this.config.baseEndpoint}/${id}/request-changes`,
      {
        method: 'PUT',
        body: { comment },
      },
    )
    return this.normalizeResponse(response)
  }

  async recallSubmission(id: string): Promise<TimeEntry> {
    const response = await this.makeRequest<TimeEntry | { data: TimeEntry }>(
      `${this.config.baseEndpoint}/${id}/recall`,
      {
        method: 'PUT',
      },
    )
    return this.normalizeResponse(response)
  }

  async getApprovalHistory(id: string): Promise<TimeEntryApprovalHistory[]> {
    const response = await this.makeRequest<
      TimeEntryApprovalHistory[] | { data: TimeEntryApprovalHistory[] }
    >(`${this.config.baseEndpoint}/${id}/history`)
    if (Array.isArray(response)) {
      return response
    }
    return response.data || []
  }

  // =====================
  // Bulk Approval Methods
  // =====================

  async bulkSubmit(
    timeEntryIds: string[],
    comment?: string,
  ): Promise<BulkApprovalResponse> {
    const response = await this.makeRequest<BulkApprovalResponse>(
      `${this.config.baseEndpoint}/bulk-submit`,
      {
        method: 'POST',
        body: { timeEntryIds, comment },
      },
    )
    return response
  }

  async bulkApprove(
    timeEntryIds: string[],
    comment?: string,
    draftEntryIds?: string[],
  ): Promise<BulkApprovalResponse> {
    const response = await this.makeRequest<BulkApprovalResponse>(
      `${this.config.baseEndpoint}/bulk-approve`,
      {
        method: 'POST',
        body: { timeEntryIds, comment, draftEntryIds },
      },
    )
    return response
  }

  async bulkReject(
    timeEntryIds: string[],
    reason: string,
  ): Promise<BulkApprovalResponse> {
    const response = await this.makeRequest<BulkApprovalResponse>(
      `${this.config.baseEndpoint}/bulk-reject`,
      {
        method: 'POST',
        body: { timeEntryIds, reason },
      },
    )
    return response
  }

  // =====================
  // Admin Status Change Methods
  // =====================

  async getCostCurrencyReview(
    id: string,
  ): Promise<{ id: string; version: number; required: boolean }> {
    return this.makeRequest(
      `${this.config.baseEndpoint}/${id}/cost-currency-review`,
    )
  }

  async verifyCostCurrency(
    id: string,
    input: {
      expectedVersion: number
      currency: string
      sourceReference: string
      reason: string
    },
  ): Promise<{ id: string; version: number; costCurrency: string }> {
    return this.makeRequest(`${this.config.baseEndpoint}/${id}/cost-currency`, {
      method: 'PATCH',
      body: input,
    })
  }

  async getBillCurrencyReview(
    id: string,
  ): Promise<{ id: string; version: number; required: boolean }> {
    return this.makeRequest(
      `${this.config.baseEndpoint}/${id}/bill-currency-review`,
    )
  }

  async verifyBillCurrency(
    id: string,
    input: {
      expectedVersion: number
      currency: string
      sourceReference: string
      reason: string
    },
  ): Promise<{ id: string; version: number; billCurrency: string }> {
    return this.makeRequest(`${this.config.baseEndpoint}/${id}/bill-currency`, {
      method: 'PATCH',
      body: input,
    })
  }

  async adminChangeStatus(
    id: string,
    status: string,
    comment?: string,
  ): Promise<TimeEntry> {
    const response = await this.makeRequest<TimeEntry | { data: TimeEntry }>(
      `${this.config.baseEndpoint}/${id}/admin-status`,
      {
        method: 'PUT',
        body: { status, comment },
      },
    )
    return this.normalizeResponse(response)
  }

  async bulkAdminChangeStatus(
    timeEntryIds: string[],
    status: string,
    comment?: string,
  ): Promise<BulkApprovalResponse> {
    const response = await this.makeRequest<BulkApprovalResponse>(
      `${this.config.baseEndpoint}/bulk-admin-status`,
      {
        method: 'POST',
        body: { timeEntryIds, status, comment },
      },
    )
    return response
  }

  async adminChangeUserPeriodStatus(
    userId: string,
    payPeriodId: string,
    status: string,
    comment?: string,
  ): Promise<BulkApprovalResponse> {
    const response = await this.makeRequest<BulkApprovalResponse>(
      `${this.config.baseEndpoint}/user-period-admin-status`,
      {
        method: 'POST',
        body: { userId, payPeriodId, status, comment },
      },
    )
    return response
  }

  // =====================
  // Team/Admin View Methods
  // =====================

  private buildTeamEntriesQueryParams(params: {
    memberId?: string
    statusTier?: string
    status?: string
    projectId?: string
    entityId?: string
    taskGroupId?: string
    bucketId?: string
    sprintId?: string
    taskId?: string
    moduleType?: string
    description?: string
    billable?: boolean
    startDate?: string
    endDate?: string
    includeActiveTimers?: boolean
    includeMetrics?: boolean
    page?: number
    limit?: number
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
  }): string {
    const queryParams = new URLSearchParams()
    if (params.memberId) queryParams.append('memberId', params.memberId)
    if (params.statusTier) queryParams.append('statusTier', params.statusTier)
    if (params.status) queryParams.append('status', params.status)
    if (params.projectId) queryParams.append('projectId', params.projectId)
    if (params.entityId) queryParams.append('entityId', params.entityId)
    if (params.taskGroupId)
      queryParams.append('taskGroupId', params.taskGroupId)
    if (params.bucketId) queryParams.append('bucketId', params.bucketId)
    if (params.sprintId) queryParams.append('sprintId', params.sprintId)
    if (params.taskId) queryParams.append('taskId', params.taskId)
    if (params.moduleType) queryParams.append('moduleType', params.moduleType)
    if (params.description)
      queryParams.append('description', params.description)
    if (params.billable !== undefined)
      queryParams.append('billable', String(params.billable))
    if (params.startDate) queryParams.append('startDate', params.startDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)
    if (params.includeActiveTimers !== undefined)
      queryParams.append(
        'includeActiveTimers',
        String(params.includeActiveTimers),
      )
    if (params.includeMetrics) queryParams.append('includeMetrics', 'true')
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())
    if (params.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder)
    return queryParams.toString()
  }

  async getTeamEntries(
    params: {
      memberId?: string
      statusTier?: string
      status?: string
      projectId?: string
      entityId?: string
      taskGroupId?: string
      bucketId?: string
      sprintId?: string
      taskId?: string
      moduleType?: string
      description?: string
      billable?: boolean
      startDate?: string
      endDate?: string
      page?: number
      limit?: number
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
    } = {},
  ): Promise<{
    data: TimeEntry[]
    total: number
    totals?: {
      totalHours: number
      billableHours: number
      nonBillableHours: number
      totalBillableAmount: number
      totalCostAmount: number
      // Pay under each person's pay basis (contractors may be paid for all
      // hours, billable hours only, or a retainer). Absent from older APIs.
      totalPaidAmount?: number
      unresolvedBillableCount?: number
      orgDefaultCount?: number
      orgDefaultApprovedCount?: number
    }
  }> {
    const queryString = this.buildTeamEntriesQueryParams(params)
    const url = `${this.config.baseEndpoint}/team${queryString ? `?${queryString}` : ''}`
    const response = await this.makeRequest<{
      data: TimeEntry[]
      total: number
      totals?: {
        totalHours: number
        billableHours: number
        nonBillableHours: number
        totalBillableAmount: number
        totalCostAmount: number
        totalPaidAmount?: number
        unresolvedBillableCount?: number
        orgDefaultCount?: number
        orgDefaultApprovedCount?: number
      }
    }>(url)
    return response
  }

  async getAllTeamEntries(
    params: Parameters<TimeEntryApiService['getTeamEntries']>[0] = {},
    pageSize = 200,
  ): Promise<Awaited<ReturnType<TimeEntryApiService['getTeamEntries']>>> {
    if (!Number.isInteger(pageSize) || pageSize <= 0) {
      throw new Error('pageSize must be a positive integer')
    }

    const scope = captureRequestScope()
    const data: TimeEntry[] = []
    let page = 1
    const maxPages = 200
    let total = 0
    let totals:
      | Awaited<ReturnType<TimeEntryApiService['getTeamEntries']>>['totals']
      | undefined

    while (page <= maxPages) {
      scope.assertCurrent()
      const response = await this.getTeamEntries({
        ...params,
        page,
        limit: pageSize,
      })
      scope.assertCurrent()
      data.push(...response.data)
      total = response.total
      totals = response.totals

      if (response.data.length < pageSize || data.length >= total) {
        return { data, total, totals }
      }
      if (page === maxPages) {
        throw new Error(
          `Time-entry pagination exceeded the ${maxPages}-page safety bound; refusing to return a truncated result`,
        )
      }
      page += 1
    }

    return { data, total, totals }
  }

  async getTeamSummary(
    params: {
      memberId?: string
      statusTier?: string
      status?: string
      projectId?: string
      entityId?: string
      taskGroupId?: string
      bucketId?: string
      sprintId?: string
      taskId?: string
      moduleType?: string
      description?: string
      billable?: boolean
      startDate?: string
      endDate?: string
      includeActiveTimers?: boolean
      /** Adds `metrics`: billable mix, money and utilization vs target. */
      includeMetrics?: boolean
    } = {},
  ): Promise<{
    members: Array<{
      userId: string
      name: string
      email: string
      totalHours: number
      billableHours: number
      approvedHours: number
      pendingHours: number
      draftHours: number
      rejectedHours: number
      entries: number
      hasActiveTimer: boolean
      activeHours: number
      currentActivity?: string
    }>
    totals: {
      totalHours: number
      billableHours: number
      approvedHours: number
      pendingHours: number
      draftHours: number
      rejectedHours: number
      totalEntries: number
      uniqueMembers: number
    }
    /** Present when requested with `includeMetrics`. */
    metrics?: TimeReportSummary
  }> {
    const queryString = this.buildTeamEntriesQueryParams(params)
    const url = `${this.config.baseEndpoint}/team/summary${queryString ? `?${queryString}` : ''}`
    return this.makeRequest(url)
  }

  async getTeamFilterOptions(): Promise<{
    projects: Array<{
      id: string
      name: string
      entityId: string | null
      entityName: string | null
    }>
    taskGroups: Array<{ id: string; name: string; projectId: string | null }>
    buckets: Array<{ id: string; name: string; projectId: string }>
    sprints: Array<{ id: string; name: string; projectId: string | null }>
    entities: Array<{ id: string; name: string }>
    members: Array<{
      id: string
      name: string
      email: string
      image: string | null
    }>
  }> {
    return this.makeRequest(`${this.config.baseEndpoint}/team/filter-options`)
  }

  async exportTimeReport(
    body: {
      format: 'csv' | 'xlsx' | 'pdf'
      dateRange?: { start?: string; end?: string }
      statusTier?: string | null
      statusFilter?: string[]
      projectScope?: { projectId: string }
      filters?: {
        memberIds?: string[]
        projectIds?: string[]
        taskGroupIds?: string[]
        bucketIds?: string[]
        sprintIds?: string[]
        roleIds?: string[]
        billable?: boolean | null
        moduleTypes?: string[]
        entityIds?: string[]
        taskId?: string
        description?: string
        workItemType?: 'PROJECT' | 'TASK' | 'TASK_GROUP'
        workItemIds?: string[]
      }
      groupBy?: string | null
      columns?: string[]
      sortBy?: string | null
      sortDirection?: 'asc' | 'desc'
    },
    options: TimeReportExportOptions = {},
  ): Promise<Blob | { jobId: string; status: string }> {
    options = { ...options, session: options.session ?? captureRequestScope() }
    const response = await this.reportExportRequest(
      `/reports/time/${options.scope === 'PERSONAL' ? 'personal/' : ''}export`,
      options,
      body,
    )
    const result =
      response.status === 202
        ? await response
            .json()
            .then(
              value =>
                (value?.data ?? value) as { jobId: string; status: string },
            )
        : await response.blob()
    options.session?.assertCurrent()
    return result
  }

  async getTimeReportExportJob(
    jobId: string,
    options: TimeReportExportOptions = {},
  ): Promise<{
    id: string
    status: 'queued' | 'processing' | 'completed' | 'failed'
    filename?: string
    error?: string
    expiresAt: string
  }> {
    options = { ...options, session: options.session ?? captureRequestScope() }
    const response = await this.reportExportRequest(
      `/reports/time/${options.scope === 'PERSONAL' ? 'personal/' : ''}export/jobs/${encodeURIComponent(jobId)}`,
      options,
    )
    const job = await response.json()
    options.session?.assertCurrent()
    return job?.data ?? job
  }

  async downloadTimeReportExportJob(
    jobId: string,
    options: TimeReportExportOptions = {},
  ): Promise<Blob> {
    options = { ...options, session: options.session ?? captureRequestScope() }
    const response = await this.reportExportRequest(
      `/reports/time/${options.scope === 'PERSONAL' ? 'personal/' : ''}export/jobs/${encodeURIComponent(jobId)}/download`,
      options,
    )
    const blob = await response.blob()
    options.session?.assertCurrent()
    return blob
  }

  private async reportExportRequest(
    path: string,
    options: TimeReportExportOptions,
    body?: unknown,
  ): Promise<Response> {
    const session = options.session ?? captureRequestScope()
    session.assertCurrent()
    const { currentOrganization, externalOrgRelationship } =
      useOrganizationStore.getState()
    if (!session.identity || !currentOrganization?.id)
      throw new Error('Choose a workspace before exporting.')
    const headers: Record<string, string> = {
      'x-platform': getPlatform(),
      'x-organization-id': currentOrganization.id,
    }
    if (currentOrganization.slug)
      headers['x-organization-slug'] = currentOrganization.slug
    if (currentOrganization.isExternalAccess && externalOrgRelationship?.id)
      headers['x-external-org-relationship-id'] = externalOrgRelationship.id
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json'
      headers['Idempotency-Key'] = options.requestId ?? crypto.randomUUID()
    }
    if (isTauri()) {
      const tokens = await desktopSecureStorage.getTokens()
      session.assertCurrent()
      if (tokens?.accessToken)
        headers.Authorization = `Bearer ${tokens.accessToken}`
    }
    session.assertCurrent()
    const response = await fetch(`${env.apiUrl}${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      credentials: 'include',
      headers,
      signal: session.signal,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    })
    session.assertCurrent()
    if (!response.ok) {
      const error = await response.json().catch(() => null)
      session.assertCurrent()
      throw new Error(
        typeof error?.message === 'string'
          ? error.message
          : 'The export is unavailable. Try again.',
      )
    }
    return response
  }

  async getPendingApprovals(
    params: {
      memberId?: string
      projectId?: string
      startDate?: string
      endDate?: string
      page?: number
      limit?: number
    } = {},
  ): Promise<{ data: TimeEntry[]; total: number }> {
    const queryParams = new URLSearchParams()
    if (params.memberId) queryParams.append('memberId', params.memberId)
    if (params.projectId) queryParams.append('projectId', params.projectId)
    if (params.startDate) queryParams.append('startDate', params.startDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())

    const url = `${this.config.baseEndpoint}/team/pending${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    const response = await this.makeRequest<{
      data: TimeEntry[]
      total: number
    }>(url)
    return response
  }

  // ===== Import =====
  // Synchronous path used by the wizard's small-file mode; large files go
  // through the org-wide ImportJob queue via importsApi.

  previewImport(
    records: Record<string, unknown>[],
    options: ImportOptions,
  ): Promise<ImportPreviewRecord[]> {
    return this.makeRequest<ImportPreviewRecord[]>(
      `${this.config.baseEndpoint}/import/preview`,
      {
        method: 'POST',
        body: { records, options },
      },
    )
  }

  importTimeEntries(data: {
    records: ImportPreviewRecord[]
    options: ImportOptions
  }): Promise<ImportResult> {
    return this.makeRequest<ImportResult>(
      `${this.config.baseEndpoint}/import`,
      {
        method: 'POST',
        body: data,
      },
    )
  }
}

// Export singleton instance using factory
export const timeEntryApi = createSingletonApi(TimeEntryApiService)

// Export class for typing
export type { TimeEntryApiService }
